import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ColumnType } from "antd/es/table";
import { Dispatch, ReactElement, SetStateAction } from "react";
import BasicTable from "@components/core/tables/BasicTable";
import TableRow from "@components/core/tables/TableRow";
import { toastWarning } from "@utils/helpers/toast-helper";
import { useTranslation } from "react-i18next";

interface SortableTableProps<T> {
  isDraggable?: boolean;
  data: T[];
  setData: Dispatch<SetStateAction<T[]>>;
  columns: ColumnType<T>[];
  className?: string;
  rowClassName?: (record: T) => string;
  isLocked?: (record: T) => boolean;
}

const SortableTable = <T extends { key: string; [key: string]: any }>(
  props: SortableTableProps<T>,
): ReactElement => {
  const { t } = useTranslation();
  const {
    isDraggable = true,
    data,
    setData,
    columns,
    className = "",
    rowClassName,
    isLocked = () => false,
  } = props;

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    const activeItem = data.find((i) => i.key === active.id);
    const overItem = data.find((i) => i.key === over.id);
    if (!activeItem || !overItem) return;
    if (isLocked(activeItem) || isLocked(overItem)) {
      toastWarning(t("table.sortable.locked"));
      return;
    }
    if (active.id !== over.id) {
      setData((previous) => {
        const activeIndex = previous.findIndex((i) => i.key === active.id);
        const overIndex = previous.findIndex((i) => i.key === over.id);
        return arrayMove(previous, activeIndex, overIndex);
      });
    }
  };

  const renderTable = () => (
    <BasicTable
      components={{
        body: {
          row: TableRow,
        },
      }}
      rowKey="key"
      pagination={false}
      columns={columns}
      dataSource={data}
      rowClassName={rowClassName}
    />
  );

  return (
    <div className={`app-table-sortable ${className}`}>
      {isDraggable ? (
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            // rowKey array
            items={data.filter((i) => !isLocked(i)).map((i) => i.key)}
            strategy={verticalListSortingStrategy}
          >
            {renderTable()}
          </SortableContext>
        </DndContext>
      ) : (
        renderTable()
      )}
    </div>
  );
};

export default SortableTable;
